/**
 * Milestone chests: the one config table (odds and amounts) and the pure roll.
 *
 * Chests are **EARNED, never bought** — nothing in this module or anywhere else
 * takes a price for one, and there is no route that turns $CHAIN into a chest.
 * The server rolls a chest once, when it is awarded, with a crypto RNG it
 * passes in as `rng(n)` (a uniform integer in [0, n)); the result is stored and
 * the client only animates it.
 *
 * Every chance below is in basis points (1/10,000) so the published odds are
 * exact integers, and the roll draws exactly one integer per decision.
 *
 * The table is Blockari's, number for number, because a chest has to mean the
 * same thing in both titles (`packages/sim/src/chests.ts` there). What differs
 * is 21 Wild's own ticket rungs and its own cosmetics.
 */

export const RARITIES = ['common', 'rare', 'epic', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];

export const rarityIndex = (r: Rarity): number => RARITIES.indexOf(r);

/**
 * What earned the chest. `rank_up` is every rank crossed; the win counts are lifetime challenge
 * wins; `weekly_top` is a top-3 place on a weekly leaderboard (one per board per place per week);
 * `welcome` is the one gift a new account gets on registering (a guest converting, or a fresh sign-up).
 */
export const CHEST_SOURCES = [
  'rank_up',
  'first_win',
  'wins_10',
  'wins_50',
  'wins_100',
  'win_streak_3',
  'login_streak_7',
  'weekly_top',
  'welcome',
] as const;
export type ChestSource = (typeof CHEST_SOURCES)[number];

/** Lifetime challenge wins that earn a chest, and the source each one is. */
export const WIN_MILESTONES: readonly { wins: number; source: ChestSource }[] = [
  { wins: 1, source: 'first_win' },
  { wins: 10, source: 'wins_10' },
  { wins: 50, source: 'wins_50' },
  { wins: 100, source: 'wins_100' },
];
/** Consecutive challenge wins that earn the first streak chest. */
export const WIN_STREAK = 3;

/**
 * Whether a run of this many consecutive wins earns a chest: at 3, at 5, and every 5 from there
 * (10, 15, 20, 25, 30…). On REACHING the number, never between: a run of 4 or 7 earns nothing.
 */
export function streakChestAt(run: number): boolean {
  return run === WIN_STREAK || (run >= 5 && run % 5 === 0);
}
/** Consecutive daily top-ups that earn a chest (every multiple of it). */
export const LOGIN_STREAK = 7;

export const BP = 10_000;

/** The free-entry rungs a chest can hold — the quick wagers (`ENTRY_FEES`), one rung per rarity. */
export const TICKET_FEES = [10, 25, 50, 100] as const;

/* --------------------------------------------------------------------------
   Cosmetics: share-card frames and titles
   -------------------------------------------------------------------------- */

export type CosmeticKind = 'frame' | 'title';

export interface Cosmetic {
  readonly id: string;
  readonly kind: CosmeticKind;
  readonly rarity: Rarity;
  /** Display name — the card table's own words. */
  readonly name: string;
}

/**
 * Six frames across the rarities and eight titles. Frames draw around the
 * share card and tint the identity pill; a title rides in the pill. The names
 * are 21 Wild's — felt, chips and the deck — where Blockari's are stonework.
 */
export const COSMETICS: readonly Cosmetic[] = [
  { id: 'frame-felt', kind: 'frame', rarity: 'common', name: 'House Felt' },
  { id: 'frame-rail', kind: 'frame', rarity: 'rare', name: 'Brass Rail' },
  { id: 'frame-neon', kind: 'frame', rarity: 'rare', name: 'Neon Pit' },
  { id: 'frame-violet', kind: 'frame', rarity: 'epic', name: 'Violet Shoe' },
  { id: 'frame-ember', kind: 'frame', rarity: 'epic', name: 'Ember Rail' },
  { id: 'frame-highroller', kind: 'frame', rarity: 'legendary', name: 'High Roller' },
  { id: 'title-dealer', kind: 'title', rarity: 'common', name: 'Dealer' },
  { id: 'title-counter', kind: 'title', rarity: 'common', name: 'Card Counter' },
  { id: 'title-streak', kind: 'title', rarity: 'rare', name: 'Streak Hunter' },
  { id: 'title-sharp', kind: 'title', rarity: 'rare', name: 'Table Sharp' },
  { id: 'title-cleaner', kind: 'title', rarity: 'epic', name: 'Pile Cleaner' },
  { id: 'title-nobust', kind: 'title', rarity: 'epic', name: 'Never Busts' },
  { id: 'title-wild', kind: 'title', rarity: 'legendary', name: 'The Wild One' },
  { id: 'title-housebreaker', kind: 'title', rarity: 'legendary', name: 'Housebreaker' },
];

export function cosmetic(id: string | null | undefined): Cosmetic | null {
  return COSMETICS.find((c) => c.id === id) ?? null;
}

/* --------------------------------------------------------------------------
   The table
   -------------------------------------------------------------------------- */

export interface RarityContents {
  /** $CHAIN, uniform in [min, max], always present. Small: these become real tokens. */
  readonly chain: { readonly min: number; readonly max: number };
  /** Chance of a free-entry ticket, and its rung. */
  readonly ticket: { readonly chanceBp: number; readonly fee: (typeof TICKET_FEES)[number] };
  /** Chance of an XP boost (2x game XP), and for how many finished games. */
  readonly boost: { readonly chanceBp: number; readonly games: number };
  /** Chance of a cosmetic (a frame or a title) from this rarity's pool. */
  readonly cosmetic: { readonly chanceBp: number };
}

export interface ChestTable {
  /**
   * Starting rarity by source. `rank_up` scales with the rank reached: the
   * first rarity whose `fromRank` the new rank index reaches, from the top.
   */
  readonly start: Readonly<Record<Exclude<ChestSource, 'rank_up' | 'weekly_top'>, Rarity>>;
  readonly rankUpStart: readonly { readonly fromRank: number; readonly rarity: Rarity }[];
  /** `weekly_top` by place on the board: 1st, 2nd, 3rd. */
  readonly weeklyTopStart: readonly [Rarity, Rarity, Rarity];
  /** Chance each tap upgrades one step (common→rare, rare→epic, epic→legendary). */
  readonly upgradeBp: readonly [number, number, number];
  readonly contents: Readonly<Record<Rarity, RarityContents>>;
  /** A cosmetic, when rolled: chance it is a frame (else a title). */
  readonly frameBp: number;
  /** The XP multiplier a boost applies to game XP. */
  readonly boostMultiplier: number;
}

export const CHEST_TABLE: ChestTable = {
  start: {
    first_win: 'common',
    wins_10: 'rare',
    wins_50: 'epic',
    wins_100: 'epic',
    win_streak_3: 'common',
    login_streak_7: 'common',
    welcome: 'common',
  },
  rankUpStart: [
    { fromRank: 5, rarity: 'epic' },
    { fromRank: 3, rarity: 'rare' },
    { fromRank: 0, rarity: 'common' },
  ],
  weeklyTopStart: ['legendary', 'epic', 'rare'],
  upgradeBp: [3000, 2500, 1500],
  contents: {
    common: {
      chain: { min: 2, max: 6 },
      ticket: { chanceBp: 2500, fee: 10 },
      boost: { chanceBp: 2500, games: 2 },
      cosmetic: { chanceBp: 1000 },
    },
    rare: {
      chain: { min: 6, max: 15 },
      ticket: { chanceBp: 5000, fee: 25 },
      boost: { chanceBp: 5000, games: 3 },
      cosmetic: { chanceBp: 3000 },
    },
    epic: {
      chain: { min: 15, max: 30 },
      ticket: { chanceBp: 8000, fee: 50 },
      boost: { chanceBp: 8000, games: 5 },
      cosmetic: { chanceBp: 6000 },
    },
    legendary: {
      chain: { min: 30, max: 60 },
      ticket: { chanceBp: BP, fee: 100 },
      boost: { chanceBp: BP, games: 8 },
      cosmetic: { chanceBp: BP },
    },
  },
  frameBp: 5000,
  boostMultiplier: 2,
};

/* --------------------------------------------------------------------------
   The roll
   -------------------------------------------------------------------------- */

export type ChestItem =
  | { readonly type: 'chain'; readonly amount: number }
  | { readonly type: 'ticket'; readonly fee: number; readonly count: number }
  | { readonly type: 'boost'; readonly games: number; readonly multiplier: number }
  | {
      readonly type: 'cosmetic';
      readonly id: string;
      readonly kind: CosmeticKind;
      readonly rarity: Rarity;
    };

export interface ChestRoll {
  readonly start: Rarity;
  /** The rarity after each tap that upgraded, in order; empty = it opens as it landed. */
  readonly steps: readonly Rarity[];
  readonly final: Rarity;
  readonly contents: readonly ChestItem[];
}

/** A uniform integer in [0, n). The server passes `crypto.randomInt`. */
export type ChestRng = (n: number) => number;

/** Weekly leaderboard places that earn a chest. */
export const WEEKLY_TOP_PLACES = 3;

/**
 * Games played in the period to appear on the $CHAIN, Best score and Win streaks boards. The server's leaderboard query
 * reads this (server/src/leaderboard.ts MIN_GAMES) and the Guide states it, so the two cannot drift. XP has no minimum.
 */
export const LEADERBOARD_MIN_GAMES = 3;
/** Rounds in the period with a 2X or better card streak to appear on the Card streaks board (server MIN_COMBOS; the Guide states it). */
export const LEADERBOARD_MIN_COMBOS = 3;

/**
 * The starting rarity. `detail` is the rank index reached for `rank_up` and the
 * place (1-based) for `weekly_top`; other sources ignore it.
 */
export function startRarity(source: ChestSource, detail = 0): Rarity {
  if (source === 'weekly_top') {
    const i = Math.max(1, Math.min(WEEKLY_TOP_PLACES, Math.floor(detail) || 1)) - 1;
    return CHEST_TABLE.weeklyTopStart[i] ?? 'rare';
  }
  const rankIndex = detail;
  if (source !== 'rank_up') return CHEST_TABLE.start[source];
  for (const r of CHEST_TABLE.rankUpStart) if (rankIndex >= r.fromRank) return r.rarity;
  return 'common';
}

function chance(rng: ChestRng, bp: number): boolean {
  if (bp >= BP) return true;
  if (bp <= 0) return false;
  return rng(BP) < bp;
}

/**
 * The cosmetic pool for a rarity, minus what the player owns or has waiting in
 * an unopened chest; when that is empty, the next rarity down, and so on. Null
 * when there is nothing left to give (the chest then holds no cosmetic).
 */
function pickCosmetic(rng: ChestRng, rarity: Rarity, exclude: ReadonlySet<string>): Cosmetic | null {
  const wantFrame = chance(rng, CHEST_TABLE.frameBp);
  for (let i = rarityIndex(rarity); i >= 0; i--) {
    const pool = COSMETICS.filter((c) => c.rarity === RARITIES[i] && !exclude.has(c.id));
    if (pool.length === 0) continue;
    // Prefer the rolled kind; take the other when this rarity has none of it left.
    const kind = pool.filter((c) => c.kind === (wantFrame ? 'frame' : 'title'));
    const from = kind.length > 0 ? kind : pool;
    return from[rng(from.length)] ?? null;
  }
  return null;
}

/**
 * Roll one chest: the upgrade taps from its starting rarity (each tap a fresh
 * draw, stopping at the first miss or at legendary), then the contents of the
 * rarity it ends on. `exclude` is the cosmetics the player already owns or has
 * waiting, so a chest never holds a duplicate.
 */
export function rollChest(
  rng: ChestRng,
  start: Rarity,
  exclude: ReadonlySet<string> = new Set(),
): ChestRoll {
  const steps: Rarity[] = [];
  let at = rarityIndex(start);
  while (at < RARITIES.length - 1 && chance(rng, CHEST_TABLE.upgradeBp[at] ?? 0)) {
    at++;
    steps.push(RARITIES[at]!);
  }
  const final = RARITIES[at]!;
  const c = CHEST_TABLE.contents[final];
  const contents: ChestItem[] = [
    { type: 'chain', amount: c.chain.min + rng(c.chain.max - c.chain.min + 1) },
  ];
  if (chance(rng, c.ticket.chanceBp)) contents.push({ type: 'ticket', fee: c.ticket.fee, count: 1 });
  if (chance(rng, c.boost.chanceBp))
    contents.push({ type: 'boost', games: c.boost.games, multiplier: CHEST_TABLE.boostMultiplier });
  if (chance(rng, c.cosmetic.chanceBp)) {
    const pick = pickCosmetic(rng, final, exclude);
    if (pick) contents.push({ type: 'cosmetic', id: pick.id, kind: pick.kind, rarity: pick.rarity });
  }
  return { start, steps, final, contents };
}

/* --------------------------------------------------------------------------
   The published odds (GET /api/chests/odds), derived from the table
   -------------------------------------------------------------------------- */

/** Parts per million: the published odds' unit (exact for this table). */
export const PPM = 1_000_000;

/** Final-rarity probabilities in ppm (summing to exactly 1,000,000) for a starting rarity. */
export function finalOdds(start: Rarity): Record<Rarity, number> {
  const out: Record<Rarity, number> = { common: 0, rare: 0, epic: 0, legendary: 0 };
  let reach = 1;
  for (let i = rarityIndex(start); i < RARITIES.length; i++) {
    const up = i < RARITIES.length - 1 ? (CHEST_TABLE.upgradeBp[i] ?? 0) / BP : 0;
    out[RARITIES[i]!] = Math.round(reach * (1 - up) * PPM);
    reach *= up;
  }
  // Any rounding remainder lands on the starting rarity so the row sums exactly.
  const sum = RARITIES.reduce((a, r) => a + out[r], 0);
  out[start] += PPM - sum;
  return out;
}

export interface ChestOdds {
  readonly purchasable: false;
  readonly rarities: readonly Rarity[];
  readonly sources: readonly {
    readonly source: ChestSource;
    /** For `rank_up`: one row per starting band. */
    readonly fromRank?: number;
    /** For `weekly_top`: one row per place on the board (1–3). */
    readonly place?: number;
    readonly start: Rarity;
    /** Final rarity, parts per million. */
    readonly finalPpm: Record<Rarity, number>;
  }[];
  readonly upgradeBp: readonly { readonly from: Rarity; readonly to: Rarity; readonly bp: number }[];
  readonly contents: Readonly<Record<Rarity, RarityContents>>;
  readonly frameBp: number;
  readonly boostMultiplier: number;
  readonly cosmetics: readonly Cosmetic[];
}

export function chestOdds(): ChestOdds {
  const sources: ChestOdds['sources'][number][] = [];
  for (const s of CHEST_SOURCES) {
    if (s === 'rank_up') {
      for (const band of [...CHEST_TABLE.rankUpStart].reverse())
        sources.push({
          source: s,
          fromRank: band.fromRank,
          start: band.rarity,
          finalPpm: finalOdds(band.rarity),
        });
    } else if (s === 'weekly_top') {
      CHEST_TABLE.weeklyTopStart.forEach((start, i) =>
        sources.push({ source: s, place: i + 1, start, finalPpm: finalOdds(start) }),
      );
    } else {
      const start = CHEST_TABLE.start[s];
      sources.push({ source: s, start, finalPpm: finalOdds(start) });
    }
  }
  return {
    purchasable: false,
    rarities: RARITIES,
    sources,
    upgradeBp: CHEST_TABLE.upgradeBp.map((bp, i) => ({
      from: RARITIES[i]!,
      to: RARITIES[i + 1]!,
      bp,
    })),
    contents: CHEST_TABLE.contents,
    frameBp: CHEST_TABLE.frameBp,
    boostMultiplier: CHEST_TABLE.boostMultiplier,
    cosmetics: COSMETICS,
  };
}

/**
 * Every rank step crossed between two XP levels — one chest each.
 *
 * The steps past the last named rank count too (Wild II, Wild III…): the
 * ladder does not stop, so neither does the reward for climbing it.
 */
export function rankStepsCrossed(levelBefore: number, levelAfter: number, span = 5): number[] {
  const step = (lv: number): number => Math.floor((Math.max(1, lv) - 1) / span);
  const out: number[] = [];
  for (let s = step(levelBefore) + 1; s <= step(levelAfter); s++) out.push(s);
  return out;
}
